import { isAcTerminalId } from "./ac";
import { isSenseTerminal } from "./sense-terminal";
import type { SimulateContext } from "./cable-current";
import { buildGraph } from "./diagnostics/graph";
import type { DefLookup } from "./catalog";
import { dcPortTerminalGroups, isInverterCharger, isPvTerminalId } from "./component-ports";
import { resolveTerminalPotentials, type TerminalPotentials } from "./terminal-voltage";
import type { Cable, ComponentDefinition, SystemNode, Terminal } from "./types";
import { resolveVoltageDomains, type VoltageDomainMap } from "./voltage-domain";

// Undefined means static sizing; live contexts must not be retained strongly.
const STATIC_VOLTAGE_STATE: SimulateContext = { componentStates: {} };

type VoltageContext = { domains: VoltageDomainMap; potentials: TerminalPotentials };
const voltageCache = new WeakMap<Record<string, SystemNode>, WeakMap<Cable[], Map<DefLookup, WeakMap<SimulateContext, VoltageContext>>>>();

/** Nominal voltage on one component side, without crossing its converter.
 * Live calls resolve the selected supply; absent state retains edit-time sizing. */
export function componentSideVoltage(
  nodeId: string,
  def: ComponentDefinition,
  direction: Terminal["direction"] | undefined,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
  fallback = 12,
  simulate?: SimulateContext,
): number {
  let byCables = voltageCache.get(nodesById);
  if (!byCables) { byCables = new WeakMap(); voltageCache.set(nodesById, byCables); }
  let byLookup = byCables.get(cables);
  if (!byLookup) { byLookup = new Map(); byCables.set(cables, byLookup); }
  let byState = byLookup.get(defLookup);
  if (!byState) { byState = new WeakMap(); byLookup.set(defLookup, byState); }
  const stateKey = simulate ?? STATIC_VOLTAGE_STATE;
  let context = byState.get(stateKey);
  if (!context) {
    const system = { name: "", version: 0, nodes: Object.values(nodesById), cables, zoneDistances: {} };
    const graph = buildGraph(system, defLookup, { simulate, excludeSense: true });
    context = { domains: resolveVoltageDomains(system, defLookup), potentials: resolveTerminalPotentials(system, defLookup, graph, simulate) };
    byState.set(stateKey, context);
  }
  const groups = direction == null
    ? dcPortTerminalGroups(def, "input")
    : dcPortTerminalGroups(def, direction);
  let exactVoltage = 0;
  for (const group of groups) {
    for (const pos of group.filter(t => t.polarity === "positive")) {
      for (const neg of group.filter(t => t.polarity !== "positive")) {
        const exact = context.potentials.voltageBetween(`${nodeId}:${pos.id}`, `${nodeId}:${neg.id}`);
        if (exact != null && exact > 0) exactVoltage = Math.max(exactVoltage, exact);
      }
    }
  }
  if (exactVoltage > 0) return exactVoltage;
  let voltage = 0;
  for (const terminal of groups.flat()) {
    if (terminal.polarity !== "positive") continue;
    const domain = context.domains.domainByTerminal.get(`${nodeId}:${terminal.id}`);
    if (domain?.hasSource) voltage = Math.max(voltage, domain.nominalV);
  }
  return voltage > 0 ? voltage : fallback;
}

/** A continuous demand must be declared explicitly. Hardware limits and surge
 * ratings do not establish operating current; watts apply only when the load
 * explicitly opts into the constant-power model. Zero is a valid declaration. */
export function hasKnownLoadOperatingCurrent(def: ComponentDefinition): boolean {
  return (def.ampDraw != null && Number.isFinite(def.ampDraw) && def.ampDraw >= 0) ||
    (def.loadCurrentModel === "constant-power" && def.watts != null &&
      Number.isFinite(def.watts) && def.watts >= 0);
}

/** Ordinary monitor power inputs consume an unspecified amount when their
 * operating draw is absent. The monitor category alone is not evidence of
 * consumption: shunt rails, signal/sense, AC and PV posts stay outside this
 * rule. An explicit zero is known; surge/terminal ratings are not continuous
 * demand. Constant-power conversion is currently supported only for loads. */
export function hasMissingConsumerCurrent(
  def: ComponentDefinition,
  wiredTerminalIds?: ReadonlySet<string>,
): boolean {
  if (def.category === "load") return !hasKnownLoadOperatingCurrent(def);
  if (def.category !== "monitor" ||
      (def.ampDraw != null && Number.isFinite(def.ampDraw) && def.ampDraw >= 0)) return false;
  // A legacy shunt can carry both positive and negative through-rails (Lynx).
  // Its input/output positive pair is not a declared consuming power port.
  const dcPositive = def.terminals.filter(t => t.polarity === "positive" &&
    !isAcTerminalId(t.id) && !isPvTerminalId(t.id) && !isSenseTerminal(def, t));
  if (dcPositive.some(t => t.direction === "output")) return false;
  // A hybrid monitor may have passive shunt rails and a separate power input.
  // Wiring only its shunt/sense terminals does not activate that consumer.
  return dcPositive.some(t => (t.direction ?? "input") === "input" &&
    (wiredTerminalIds == null || wiredTerminalIds.has(t.id)));
}

/** Regulated loads retain their declared power as bus voltage changes (P=VI).
 * Other loads keep their declared amps; watts alone never opts a device in.
 * Surge is specified at the reference voltage, so it follows the same ratio. */
export function loadCurrentsAtVoltage(
  def: ComponentDefinition,
  operatingV: number,
): { draw: number | undefined; surge: number | undefined } {
  if (def.category !== "load" || def.loadCurrentModel !== "constant-power" ||
      def.watts == null || !Number.isFinite(def.watts) || def.watts < 0 ||
      !Number.isFinite(operatingV) || operatingV <= 0) {
    return { draw: def.ampDraw, surge: def.ampSurge };
  }
  const referenceV = def.voltage != null && Number.isFinite(def.voltage) && def.voltage > 0 ? def.voltage : 12;
  return {
    draw: def.watts / operatingV,
    surge: def.ampSurge == null ? undefined : def.ampSurge * referenceV / operatingV,
  };
}

export function componentLoadCurrents(
  def: ComponentDefinition,
  nodeId: string,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
  simulate?: SimulateContext,
): { draw: number | undefined; surge: number | undefined } {
  if (def.category !== "load" || def.loadCurrentModel !== "constant-power") {
    return { draw: def.ampDraw, surge: def.ampSurge };
  }
  return loadCurrentsAtVoltage(def, componentSideVoltage(nodeId, def, undefined, nodesById, cables, defLookup, def.voltage ?? 12, simulate));
}

/** A DC/DC input carries output power / (input voltage × efficiency), not
 * output amps. Unknown efficiency keeps the existing documented 90% planning
 * assumption, now explicit in current rather than hidden in fuse headroom.
 * MPPT PV inputs are sized from the array; AC inputs belong to the AC model.
 * Returns undefined for those non-DC/DC parts. */
export function dcDcInputCurrent(
  def: ComponentDefinition,
  nodeId: string,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
  outputA: number,
  simulate?: SimulateContext,
): number | undefined {
  if (def.category !== "charger" || isInverterCharger(def) || !def.terminals.some(t =>
    t.direction === "input" && t.polarity === "positive" &&
    !isAcTerminalId(t.id) && !isPvTerminalId(t.id),
  )) return undefined;
  const inputV = componentSideVoltage(nodeId, def, "input", nodesById, cables, defLookup, def.inputVoltage ?? 12, simulate);
  const outputV = componentSideVoltage(nodeId, def, "output", nodesById, cables, defLookup, def.outputVoltage ?? 12, simulate);
  const declared = def.conversionEfficiency;
  const efficiency = declared != null && declared > 0 && declared <= 1 ? declared : 0.9;
  const inputA = Math.max(0, outputA) * outputV / (inputV * efficiency);
  return def.maxInputA != null && def.maxInputA > 0 ? Math.min(inputA, def.maxInputA) : inputA;
}
